import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import { Container } from 'reactstrap';

import AppMeta from 'components/App/Meta';
import AppBreadcrumb from 'components/App/Breadcrumb';
import ModalEditPolicyCancelRoomFlex from 'components/Modals/ModalEditPolicyCancelRoomFlex';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import { LOCATIONS } from 'constants/index';
import images from 'assets/images';
import classNames from 'classnames/bind';

import styles from './CancellationPolicy.module.scss';
import CreateCancelPolicy from './CreateCancelPolicy/CreateCancelPolicy';
import CancellationPolicyItem from './CancellationPolicyItem/CancellationPolicyItem';

const cx = classNames.bind(styles);

function CancellationPolicy(props) {
  const {
    placeDetail,
    isLoadingEditCancelPolicy,
    actionGetPlaceDetail,
    actionClearPlaceDetail,
    actionClearListRoomsOfPlace,
    actionEditCancelPolicy,
  } = props;

  const [isOpenModalCancelRoomFlex, setIsOpenModalCancelRoomFlex] =
    useState(false);
  const [policyForModal, setPolicyForModal] = useState(null);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');

  const cancelBookingPolicy = React.useMemo(
    () => placeDetail?.cancelBookingPolicy ?? [],
    [placeDetail],
  );

  const onToggleModalCancelRoomFlex = useCallback(() => {
    setIsOpenModalCancelRoomFlex((prev) => {
      const nextOpen = !prev;
      if (!nextOpen) {
        setPolicyForModal(null);
      }
      return nextOpen;
    });
  }, []);

  const onOpenCreateCancelPolicyModal = useCallback(() => {
    setPolicyForModal(null);
    setIsOpenModalCancelRoomFlex(true);
  }, []);

  const onOpenEditCancelPolicyModal = useCallback((policy) => {
    setPolicyForModal(policy);
    setIsOpenModalCancelRoomFlex(true);
  }, []);

  const mapCancelBookingPolicyPayload = useCallback(
    (policies) => policies.map((item) => ({
      _id: item._id,
      freeBeforeDays: item.freeBeforeDays,
      cancelPenalty: item.cancelPenalty,
      absentPenalty: item.absentPenalty,
      startAt: item.startAt,
      endAt: item.endAt,
      isApplied: item.isApplied,
    })),
    [],
  );

  const onApplyCancelPolicy = useCallback(
    (policy) => {
      const nextCancelBookingPolicy = cancelBookingPolicy.map((item) => ({
        ...item,
        isApplied: item._id === policy._id,
      }));

      actionEditCancelPolicy({
        placeId,
        cancelBookingPolicy: mapCancelBookingPolicyPayload(nextCancelBookingPolicy),
        onCallBackSave: () => actionGetPlaceDetail({ placeId }),
      });
    },
    [
      actionEditCancelPolicy,
      actionGetPlaceDetail,
      cancelBookingPolicy,
      mapCancelBookingPolicyPayload,
      placeId,
    ],
  );

  const onOpenDeleteCancelPolicyModal = useCallback((policy) => {
    setPolicyToDelete(policy);
    setIsOpenDeleteModal(true);
  }, []);

  const onCloseDeleteCancelPolicyModal = useCallback(() => {
    setIsOpenDeleteModal(false);
    setPolicyToDelete(null);
  }, []);

  const onConfirmDeleteCancelPolicy = useCallback(() => {
    if (!policyToDelete) return;

    const nextCancelBookingPolicy = cancelBookingPolicy.filter(
      (item) => item._id !== policyToDelete._id,
    );

    actionEditCancelPolicy({
      placeId,
      cancelBookingPolicy: mapCancelBookingPolicyPayload(nextCancelBookingPolicy),
      onCallBackSave: () => {
        actionGetPlaceDetail({ placeId });
        onCloseDeleteCancelPolicyModal();
      },
    });
  }, [
    actionEditCancelPolicy,
    actionGetPlaceDetail,
    cancelBookingPolicy,
    mapCancelBookingPolicyPayload,
    onCloseDeleteCancelPolicyModal,
    placeId,
    policyToDelete,
  ]);

  useEffect(() => {
    if (placeId) {
      actionGetPlaceDetail({ placeId });
    }
    return () => {
      actionClearPlaceDetail();
      actionClearListRoomsOfPlace();
    };
  }, [
    actionClearListRoomsOfPlace,
    actionClearPlaceDetail,
    actionGetPlaceDetail,
    placeId,
  ]);

  const breadcrumbList = [
    {
      name: 'Chỗ nghỉ',
      isActive: false,
      link: `${LOCATIONS.LOCATION_DETAIL.DEFAULT}?place=${placeId}`,
    },
    {
      name: 'Chính sách huỷ phòng',
      isActive: true,
      link: '',
    },
  ];

  return (
    <div className="page-content">
      <AppMeta name="Chính sách huỷ phòng" />
      <Container fluid>
        <AppBreadcrumb
          pageTitle="Chính sách huỷ phòng"
          breadcrumbList={breadcrumbList}
        />

        <div className={cx('Section')}>
          {cancelBookingPolicy.map((item) => (
            <CancellationPolicyItem
              key={item._id}
              policy={item}
              onEdit={onOpenEditCancelPolicyModal}
              onApply={onApplyCancelPolicy}
              onDelete={onOpenDeleteCancelPolicyModal}
            />
          ))}
          <CreateCancelPolicy
            onClick={onOpenCreateCancelPolicyModal}
            cancelBookingPolicyList={cancelBookingPolicy}
          />
        </div>
      </Container>

      <ModalEditPolicyCancelRoomFlex
        isOpen={isOpenModalCancelRoomFlex}
        onToggleModal={onToggleModalCancelRoomFlex}
        actionEditCancelPolicy={actionEditCancelPolicy}
        actionGetPlaceDetail={actionGetPlaceDetail}
        cancelBookingPolicy={policyForModal ?? {}}
        cancelBookingPolicyList={cancelBookingPolicy}
        isCreateMode={!policyForModal}
      />

      <ModalRequestAction
        isOpen={isOpenDeleteModal}
        icon={images.modalGarbageCan.default}
        textRequest={'Bạn có chắc chắn muốn xoá \n chính sách huỷ phòng này không?'}
        onCloseModal={onCloseDeleteCancelPolicyModal}
        onConfirm={onConfirmDeleteCancelPolicy}
        isLoadingConfirm={isLoadingEditCancelPolicy}
      />
    </div>
  );
}

CancellationPolicy.propTypes = {
  placeDetail: PropTypes.instanceOf(Object),
  isLoadingEditCancelPolicy: PropTypes.bool,
  actionGetPlaceDetail: PropTypes.func,
  actionClearPlaceDetail: PropTypes.func,
  actionClearListRoomsOfPlace: PropTypes.func,
  actionEditCancelPolicy: PropTypes.func,
};

CancellationPolicy.defaultProps = {
  placeDetail: {},
  isLoadingEditCancelPolicy: false,
  actionGetPlaceDetail: () => {},
  actionClearPlaceDetail: () => {},
  actionClearListRoomsOfPlace: () => {},
  actionEditCancelPolicy: () => {},
};

export default CancellationPolicy;
